// Extracted from Dashboard/index.web.jsx on 2026-10-02.
import { useEffect, useState } from 'react';
import { addViewportResizeListeners } from 'utils/layout/viewportResize.utils';
import dashboardConfig from '../config/dashboard.config';
import type { DashboardRowHeightOptions } from '../types/dashboard.types';

const useDashboardRowHeight = ({
  dashboardGridRef,
  widgetHeight,
  widgetCount,
  canEditWidgets,
  showAnalyticHeaders,
  weatherAlertCount,
  fusionAlertCount,
  totalAlarmsCount,
}: DashboardRowHeightOptions) => {
  const [dashboardRowHeight, setDashboardRowHeight] = useState(widgetHeight);
  useEffect(() => {
    const targetVisibleRows = dashboardConfig['visible-rows'];
    const rowGap = dashboardConfig['grid-gap'];
    const bottomPadding = dashboardConfig['bottom-padding'];
    const minRowHeight = dashboardConfig['minimum-row-height'];
    const settlePassDelays = dashboardConfig['row-height-settle-delays'];
    let rafId: number | null = null;
    const settleTimers: number[] = [];
    let scrollContainer: HTMLElement | null = null;
    let lastObservedGridWidth: number | null = null;

    const findScrollContainer = (node: HTMLElement) => {
      if (!node || typeof window === 'undefined') {
        return null;
      }

      let current = node.parentElement;
      while (current) {
        const styles = window.getComputedStyle(current);
        const overflowY = styles.overflowY || '';
        const overflow = styles.overflow || '';
        if (/(auto|scroll|overlay)/i.test(`${overflowY} ${overflow}`)) {
          return current;
        }
        current = current.parentElement;
      }

      return null;
    };

    const getStableTopOffset = (node: HTMLElement) => {
      const rect = node.getBoundingClientRect();

      if (scrollContainer instanceof HTMLElement) {
        const containerRect = scrollContainer.getBoundingClientRect();
        return rect.top - containerRect.top + scrollContainer.scrollTop;
      }

      return rect.top + (window.scrollY || window.pageYOffset || 0);
    };

    const getViewportHeight = () => {
      if (scrollContainer instanceof HTMLElement) {
        return scrollContainer.clientHeight;
      }

      return window.innerHeight;
    };

    const updateDashboardRowHeight = () => {
      const gridContainer = dashboardGridRef.current;
      if (!gridContainer) {
        return;
      }

      if (!scrollContainer) {
        scrollContainer = findScrollContainer(gridContainer);
      }

      const stableTopOffset = getStableTopOffset(gridContainer);
      const availableHeight = getViewportHeight() - stableTopOffset - bottomPadding;
      if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
        return;
      }

      const calculatedHeight = Math.floor(
        (availableHeight - rowGap * (targetVisibleRows - 1)) / targetVisibleRows,
      );
      const clampedHeight = Math.max(minRowHeight, Math.min(widgetHeight, calculatedHeight));

      setDashboardRowHeight((prev) => (Math.abs(prev - clampedHeight) > 1 ? clampedHeight : prev));
    };

    const scheduleRowHeightUpdate = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateDashboardRowHeight();
      });
    };

    const clearSettledRowHeightUpdates = () => {
      while (settleTimers.length > 0) {
        const timerId = settleTimers.pop();
        window.clearTimeout(timerId);
      }
    };

    const scheduleSettledRowHeightUpdates = () => {
      clearSettledRowHeightUpdates();
      settlePassDelays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          scheduleRowHeightUpdate();
        }, delay);
        settleTimers.push(timerId);
      });
    };

    scheduleRowHeightUpdate();
    scheduleSettledRowHeightUpdates();
    window.addEventListener('resize', scheduleRowHeightUpdate);
    const removeViewportResizeBridge = addViewportResizeListeners(
      () => {
        scheduleSettledRowHeightUpdates();
        window.dispatchEvent(new Event('resize'));
      },
      {
        includeWindowResize: false,
        includeOrientationChange: false,
      },
    );

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        const observedWidth = entries?.[0]?.contentRect?.width;
        if (Number.isFinite(observedWidth)) {
          const roundedWidth = Math.round(observedWidth);
          if (
            lastObservedGridWidth !== null &&
            Math.abs(roundedWidth - lastObservedGridWidth) < 1
          ) {
            return;
          }
          lastObservedGridWidth = roundedWidth;
        }

        scheduleRowHeightUpdate();
        scheduleSettledRowHeightUpdates();
      });

      if (dashboardGridRef.current) {
        resizeObserver.observe(dashboardGridRef.current);
      }
    }

    return () => {
      window.removeEventListener('resize', scheduleRowHeightUpdate);
      removeViewportResizeBridge();
      clearSettledRowHeightUpdates();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    dashboardGridRef,
    showAnalyticHeaders,
    canEditWidgets,
    widgetHeight,
    widgetCount,
    weatherAlertCount,
    fusionAlertCount,
    totalAlarmsCount,
  ]);

  return dashboardRowHeight;
};

export default useDashboardRowHeight;
